import { extractErrorMessage, failure, success } from 'utils/errorHandling'
import { useCallback, useEffect, useState } from 'react'

import useInnovaAuth from 'components/common/hooks/useInnovaAuth'
import useInnovaAxios from 'components/common/hooks/useInnovaAxios'

export function useDataConfig() {
  const getProDirectionalAccessRequest = useInnovaAxios({ url: '/prodirectional/hasAccess' })
  const getTurboDrillAccessRequest = useInnovaAxios({ url: '/turbodrill/hasAccess' })
  const updateConfigRequest = useInnovaAxios({ url: '/dataAcq/updateConfigs' })
  const stopReadRequest = useInnovaAxios({ url: '/dataAcq/stopRead' })
  const startReadRequest = useInnovaAxios({ url: '/dataAcq/startRead' })
  const downloadDataRequest = useInnovaAxios({ url: '/dataAcq/downloadData' })
  const getDropDownListsRequest = useInnovaAxios({ url: '/dataAcq/getDropDownLists' })
  const clearDataTablesRequest = useInnovaAxios({ url: '/dataAcq/clearDataTables' })
  const reprocessWellRequest = useInnovaAxios({ url: '/dataAcq/proc/reprocessWell' })
  const getDataAcqConfigRequest = useInnovaAxios({ url: '/dataAcq/getConfigs' })
  const archiveDataRequest = useInnovaAxios({ url: '/dataAcq/archiveData' })
  const getDefaultDataSourcesRequest = useInnovaAxios({ url: '/admin/defaultDataSources/getDefaultDataSources' })
  const updateDefaultDataSourcesRequest = useInnovaAxios({ url: '/admin/defaultDataSources/updateDefaultDataSources' })
  const syncReportingDataRequest = useInnovaAxios({ url: '/icds/syncReporting' })
  const syncOffsetsRequest = useInnovaAxios({ url: '/icds/syncOffsets' })
  const syncWellsAndReportingRequest = useInnovaAxios({ url: '/icds/syncWellsAndReporting' })
  const importNovWellDataRequest = useInnovaAxios({ url: '/novWellData/importHistoricalData' })
  const importAllHistoricalDataRequest = useInnovaAxios({ url: '/novWellData/importAllHistoricalData' })
  const importWitsmlWellDataRequest = useInnovaAxios({ url: '/dataAcq/witsml/importLogData' })

  const [dropDowns, setDropDowns] = useState([])
  const { user } = useInnovaAuth()

  const [isSyncingWellsAndReporting, setIsSyncingWellsAndReporting] = useState(false)
  const [isSyncingOffsets, setIsSyncingOffsets] = useState(false)
  const [isSyncingReportingData, setIsSyncingReportingData] = useState(false)
  const [isToggling, setIsToggling] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  const [isLoadingDropDowns, setIsLoadingDropDowns] = useState(false)
  const [isLoadingCfg, setIsLoadingCfg] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isArchiving, setIsArchiving] = useState(false)
  const [isReprocessing, setIsReprocessing] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isFetchingDefaults, setIsFetchingDefaults] = useState(false)
  const [isUpdatingDefaults, setIsUpdatingDefaults] = useState(false)
  const [isGettingTurboDrillAccess, setIsGettingTurboDrillAccess] = useState(false)
  const [isGettingProDirectionalAccess, setIsGettingProDirectionalAccess] = useState(false)
  const [isImportingNovWellData, setIsImportingNovWellData] = useState(false)
  const [isImportingAllHistoricalData, setIsImportingAllHistoricalData] = useState(false)
  const [isImportingWitsmlWellData, setIsImportingWitsmlWellData] = useState(false)

  useEffect(() => {
    fetchDropDowns()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  /* ============================================================
     Fetch Dropdowns
     ============================================================ */

  const fetchDropDowns = useCallback(async () => {
    setIsLoadingDropDowns(true)
    try {
      const res = await getDropDownListsRequest()

      if (res?.error) {
        return failure(extractErrorMessage(res.error, 'Failed to fetch dropdowns'))
      }

      if (res?.data) {
        setDropDowns(res.data)
      }

      return success('Dropdowns loaded')
    } finally {
      setIsLoadingDropDowns(false)
    }
  }, [getDropDownListsRequest])

  /* ============================================================
     Get Data Acquisition Config
     ============================================================ */

  const getDataAcqConfig = useCallback(
    async (organization, wellName) => {
      if (!organization || !wellName) {
        return failure('Invalid organization or well name')
      }

      setIsLoadingCfg(true)
      try {
        const payload = {
          wellHeader: JSON.stringify([{ organization, actualWell: wellName }]),
        }

        const res = await getDataAcqConfigRequest(payload)

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to fetch data acquisition config'))
        }

        if (!res?.data || !Array.isArray(res.data) || res.data.length === 0) {
          return failure('No data acquisition config found for the specified well')
        }

        return success('Data acquisition config loaded', res?.data[0] ?? null)
      } finally {
        setIsLoadingCfg(false)
      }
    },
    [getDataAcqConfigRequest],
  )

  /* ============================================================
     Get Data Acquisition Config
     ============================================================ */

  const getAllDataAcqConfigs = useCallback(
    async (wellHeaders) => {
      if (!wellHeaders) {
        return failure('Invalid payloaad')
      }

      setIsLoadingCfg(true)
      try {
        const payload = {
          wellHeader: JSON.stringify(wellHeaders),
        }

        const res = await getDataAcqConfigRequest(payload)

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to fetch data acquisition config'))
        }

        if (!res?.data || !Array.isArray(res.data) || res.data.length === 0) {
          return failure('No data acquisition config found for the specified well')
        }

        return success('Data acquisition config loaded', res?.data ?? [])
      } finally {
        setIsLoadingCfg(false)
      }
    },
    [getDataAcqConfigRequest],
  )

  /* ============================================================
     Update Data Config
     ============================================================ */

  const updateDataConfig = useCallback(
    async (dataConfig) => {
      if (!dataConfig) {
        return failure('Invalid data config')
      }

      dataConfig.dataSources.turboDrillSettings.wellId = String(dataConfig.dataSources.turboDrillSettings.wellId)
      dataConfig.dataSources.proDirectionalSettings.wellId = String(
        dataConfig.dataSources.proDirectionalSettings.wellId,
      )

      let payload = {
        uid: dataConfig.uid,
        actualWell: dataConfig.actualWell,
        dataInput: dataConfig.dataInput,
        dataStore: dataConfig.dataStore,
        witsmlServer: dataConfig.witsmlServer,
        readInterval: dataConfig?.readInterval,
        rpm: dataConfig.drillingThresholds.rpm,
        pressure: dataConfig.drillingThresholds.pressure,
        flow: dataConfig.drillingThresholds.flow,
        hkld: dataConfig.drillingThresholds.hkld,
        onBtmDelta: dataConfig.drillingThresholds.onBtmDelta,
        wob: dataConfig.drillingThresholds.wob,
        rpmBufferSize: dataConfig.drillingThresholds.rpmBufferSize,
        rpmBufferThreshold: dataConfig.drillingThresholds.rpmBufferThreshold,
        dynamicThresholds: dataConfig.drillingThresholds.dynamicThreshold,
        autoSlideSheet: dataConfig.autoSlideSheet,
        useAdvancedSlideDetection: dataConfig.drillingThresholds.useAdvancedSlideDetection,
        motorStallPressThresh: dataConfig.drillingThresholds.motorStallPressThresh,
        unitPrefs: dataConfig.unitPrefs,
        witsmlWell: JSON.stringify(dataConfig.witsmlWell),
        witsmlWellbore: JSON.stringify(dataConfig.witsmlWellBore),
        witsmlLogs: JSON.stringify(dataConfig.witsmlLogs),
        witsmlTrajectory: JSON.stringify(dataConfig.witsmlTrajectory),
        witsmlMapping: JSON.stringify(dataConfig.witsmlMapping),
        dataSources: JSON.stringify(dataConfig.dataSources),
      }

      setIsUpdating(true)
      try {
        const res = await updateConfigRequest(payload)
        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to update data config'))
        }
        return success('Data config updated successfully')
      } finally {
        setIsUpdating(false)
      }
    },
    [updateConfigRequest],
  )

  /* ============================================================
     Re-Process Data
     ============================================================ */

  const reProcessData = useCallback(
    async (data) => {
      if (!data) {
        return failure('Invalid reprocess data')
      }

      setIsReprocessing(true)
      try {
        const payload = {
          orgName: data.organization,
          wellName: data.actualWell,
          options: JSON.stringify(data.reprocessOptions),
        }

        const res = await reprocessWellRequest(payload)

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to reprocess data'))
        }

        return success('Reprocessing started. An email will be sent when complete')
      } finally {
        setIsReprocessing(false)
      }
    },
    [reprocessWellRequest],
  )

  /* ============================================================
     Download Data
     ============================================================ */

  const downloadData = useCallback(
    async (dataConfig) => {
      if (!dataConfig) {
        return failure('Invalid data config')
      }

      setIsDownloading(true)
      try {
        const res = await downloadDataRequest(dataConfig)

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to download data'))
        }

        if (res?.data?.status !== 'OK') {
          return failure(res?.data?.message || 'Failed to download data')
        }

        return success(res?.data?.message || 'Data download started. An email will be sent when complete')
      } finally {
        setIsDownloading(false)
      }
    },
    [downloadDataRequest],
  )

  /* ============================================================
     Clear Data
     ============================================================ */

  const clearData = useCallback(
    async (dataConfig) => {
      if (!dataConfig) {
        return failure('Invalid data config')
      }

      setIsClearing(true)
      try {
        const res = await clearDataTablesRequest({ orgName: dataConfig.organization, wellName: dataConfig.actualWell })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to clear data'))
        }

        return success('Data cleared successfully')
      } finally {
        setIsClearing(false)
      }
    },
    [clearDataTablesRequest],
  )

  /* ============================================================
     Archive Data
     ============================================================ */

  const archiveData = useCallback(
    async (dataConfig) => {
      if (!dataConfig) {
        return failure('Invalid data config')
      }

      setIsArchiving(true)
      try {
        const res = await archiveDataRequest({ uid: dataConfig.uid })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to archive data'))
        }

        return success('Data archived successfully')
      } finally {
        setIsArchiving(false)
      }
    },
    [archiveDataRequest],
  )

  /* ============================================================
     Toggle Start / Stop
     ============================================================ */

  const toggleStartStop = useCallback(
    async (dataConfig) => {
      if (!dataConfig) {
        return failure('Invalid data config')
      }

      let action = dataConfig.readStatus === 'STOPPED' || dataConfig.readStatus === 'AUTOSTOPPED' ? 'start' : 'stop'

      setIsToggling(true)
      try {
        const res = action === 'start' ? await startReadRequest(dataConfig) : await stopReadRequest(dataConfig)

        if (res?.error) {
          return failure(extractErrorMessage(res.error, `Failed to ${action}`))
        }

        dataConfig.readStatus = action === 'start' ? 'RUNNING' : 'STOPPED'
        dataConfig.startTime = action === 'start' ? new Date().toISOString() : dataConfig.startTime
        dataConfig.stopTime = action === 'stop' ? new Date().toISOString() : ''
        dataConfig.startedBy = action === 'start' ? user?.name : dataConfig.startedBy

        return success(`Data ${action}ed successfully`, dataConfig)
      } finally {
        setIsToggling(false)
      }
    },
    [startReadRequest, stopReadRequest, user],
  )

  /* ============================================================
     Fetch Default Data Sources
     ============================================================ */

  const fetchDefaultDataSources = useCallback(
    async (organization) => {
      setIsFetchingDefaults(true)
      try {
        const res = await getDefaultDataSourcesRequest({ organization })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to fetch default data sources'))
        }

        return success('Default data sources fetched successfully', res?.data)
      } finally {
        setIsFetchingDefaults(false)
      }
    },
    [getDefaultDataSourcesRequest],
  )

  /* ============================================================
     Sync Reporting Data
     ============================================================ */
  const syncReportingData = useCallback(
    async (wellName, organization) => {
      if (typeof organization !== 'string' || organization.trim() === '') {
        return failure('Invalid organization')
      }

      if (typeof wellName !== 'string' || wellName.trim() === '') {
        return failure('Invalid well name')
      }

      setIsSyncingReportingData(true)
      try {
        const res = await syncReportingDataRequest({
          wellHeader: JSON.stringify({
            actualWell: wellName,
            organization,
          }),
        })
        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to sync reporting data'))
        }
        return success('Reporting data synced successfully')
      } finally {
        setIsSyncingReportingData(false)
      }
    },
    [syncReportingDataRequest],
  )

  /* ============================================================
     Sync Offsets
     ============================================================ */
  const syncOffsets = useCallback(
    async (wellName, organization) => {
      if (typeof organization !== 'string' || organization.trim() === '') {
        return failure('Invalid organization')
      }

      if (typeof wellName !== 'string' || wellName.trim() === '') {
        return failure('Invalid well name')
      }

      setIsSyncingOffsets(true)
      try {
        const res = await syncOffsetsRequest({ wellHeader: JSON.stringify({ actualWell: wellName, organization }) })
        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to sync offsets'))
        }
        return success('Offsets synced successfully')
      } finally {
        setIsSyncingOffsets(false)
      }
    },
    [syncOffsetsRequest],
  )

  /* ============================================================
     Sync Wells and Reporting
     ============================================================ */
  const syncWellsAndReporting = useCallback(async () => {
    setIsSyncingWellsAndReporting(true)
    try {
      const res = await syncWellsAndReportingRequest({
        options: JSON.stringify({
          syncWells: true,
          syncReporting: true,
          overwriteExisitingWells: false,
          pullOffsets: false,
        }),
      })

      if (res?.error) {
        return failure(extractErrorMessage(res.error, 'Failed to sync wells and reporting'))
      }

      return success('Wells and reporting sync job queued successfully. An email will be sent when complete')
    } finally {
      setIsSyncingWellsAndReporting(false)
    }
  }, [syncWellsAndReportingRequest])

  /* ============================================================
     Update Default Data Sources
     ============================================================ */

  const updateDefaultDataSources = useCallback(
    async (dataSources, organization) => {
      setIsUpdatingDefaults(true)

      try {
        const res = await updateDefaultDataSourcesRequest({ settings: JSON.stringify(dataSources), organization })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to update default data sources'))
        }

        return success('Default data sources updated successfully')
      } finally {
        setIsUpdatingDefaults(false)
      }
    },
    [updateDefaultDataSourcesRequest],
  )

  /* ============================================================
     Get Turbo Drill Access
     ============================================================ */

  const getTurboDrillAccess = useCallback(
    async (organization) => {
      if (!organization) {
        return failure('Invalid organization')
      }

      setIsGettingTurboDrillAccess(true)
      try {
        const res = await getTurboDrillAccessRequest()

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to fetch turbo drill access'))
        }

        return success('fetched turbo drill access', res?.data?.hasAccess ?? false)
      } finally {
        setIsGettingTurboDrillAccess(false)
      }
    },
    [getTurboDrillAccessRequest],
  )

  /* ============================================================
     Get Pro Directional Access
     ============================================================ */

  const getProDirectionalAccess = useCallback(
    async (organization) => {
      if (!organization) {
        return failure('Invalid organization')
      }

      setIsGettingProDirectionalAccess(true)
      try {
        const res = await getProDirectionalAccessRequest()

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to fetch pro directional access'))
        }

        return success('fetched pro directional access', res?.data?.hasAccess ?? false)
      } finally {
        setIsGettingProDirectionalAccess(false)
      }
    },
    [getProDirectionalAccessRequest],
  )

  /* ============================================================
     Import NOV EDR Data
     ============================================================ */
  const importNovWellData = useCallback(
    async (dataConfig) => {
      if (!dataConfig) {
        return failure('Invalid data config')
      }
      if (isImportingNovWellData) {
        return failure('NOV EDR data import is already in progress')
      }
      setIsImportingNovWellData(true)

      try {
        const res = await importNovWellDataRequest({
          wellName: dataConfig.actualWell,
          wellId: dataConfig?.dataSources?.novWellDataSettings?.wellId,
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to import NOV EDR data'))
        }

        return success('NOV EDR data import queued successfully. An email will be sent when complete')
      } finally {
        setIsImportingNovWellData(false)
      }
    },
    [importNovWellDataRequest, isImportingNovWellData],
  )

  /* ============================================================
     Import NOV EDR Data for All Wells
     ============================================================ */
  const importAllHistoricalEDRData = useCallback(
    async (dataConfig) => {
      if (!dataConfig) {
        return failure('Invalid data config')
      }
      if (isImportingAllHistoricalData) {
        return failure('NOV EDR data import is already in progress')
      }
      setIsImportingAllHistoricalData(true)
      try {
        const res = await importAllHistoricalDataRequest({
          wellName: dataConfig.actualWell,
          wellId: dataConfig?.dataSources?.novWellDataSettings?.wellId,
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to import NOV EDR data'))
        }

        return success('NOV EDR data import queued successfully. An email will be sent when complete')
      } finally {
        setIsImportingAllHistoricalData(false)
      }
    },
    [importAllHistoricalDataRequest, isImportingAllHistoricalData],
  )

  /* ============================================================
     Import WITSML EDR Data
     ============================================================ */
  const importWitsmlWellData = useCallback(
    async (dataConfig) => {
      if (!dataConfig) {
        return failure('Invalid data config')
      }
      if (isImportingWitsmlWellData) {
        return failure('WITSML EDR data import is already in progress')
      }

      let payload = {
        actualWell: dataConfig.actualWell,
        witsmlWellName: dataConfig.witsmlWellName,
        id: dataConfig.witsmlServerUid, // id will allow for auto-discovery of the witsml server params
        // wellUuid: dataConfig.witsmlSettings.wellUuid,
        // wellBoreUuid: dataConfig.witsmlSettings.wellBoreUuid,
        // logUuid: dataConfig.witsmlSettings.uuid,
      }

      setIsImportingWitsmlWellData(true)

      try {
        const res = await importWitsmlWellDataRequest(payload)

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to import WITSML EDR data'))
        }

        return success('WITSML EDR data import queued successfully. An email will be sent when complete')
      } finally {
        setIsImportingWitsmlWellData(false)
      }
    },
    [importWitsmlWellDataRequest, isImportingWitsmlWellData],
  )

  return {
    isLoadingDropDowns,
    isLoadingCfg,
    isUpdating,
    isSyncingReportingData,
    isReprocessing,
    isDownloading,
    isClearing,
    dropDowns,
    isArchiving,
    isToggling,
    isFetchingDefaults,
    isUpdatingDefaults,
    isGettingTurboDrillAccess,
    isGettingProDirectionalAccess,
    isSyncingOffsets,
    isSyncingWellsAndReporting,
    getDataAcqConfig,
    getAllDataAcqConfigs,
    updateDataConfig,
    reProcessData,
    downloadData,
    syncReportingData,
    clearData,
    archiveData,
    toggleStartStop,
    fetchDefaultDataSources,
    updateDefaultDataSources,
    getTurboDrillAccess,
    getProDirectionalAccess,
    syncOffsets,
    syncWellsAndReporting,
    importNovWellData,
    importWitsmlWellData,
    importAllHistoricalEDRData,
  }
}

export default useDataConfig
